<script setup lang="ts">
import { computed } from 'vue'

import type { ReportColumn } from '../../models/column.types'
import type { SavedReportFilterOption } from '../../models/filter.types'
import { isNumericReportColumn } from '../../service/reportColumnKind'
import MetricLabel from '../MetricLabel.vue'

import { useI18n } from '@/shared/i18n'
import { Icon } from '@/shared/ui'

const props = defineProps<{
  columns: readonly ReportColumn[]
  savedFilters: readonly SavedReportFilterOption[]
  query: string
}>()
const emit = defineEmits<{
  selectSaved: [saved: SavedReportFilterOption]
  removeSaved: [id: string]
  selectColumn: [column: ReportColumn]
}>()

const { t, locale } = useI18n()
const searchableColumns = computed(() =>
  props.columns.filter((column) => !column.invisible && !column.isImage),
)
const filteredSaved = computed(() => props.savedFilters.filter((saved) => matches(saved.name)))
const groups = computed(() => [
  {
    key: 'dimensions',
    columns: searchableColumns.value.filter(
      (column) => !isNumericReportColumn(column) && !column.is_label && matches(column.header),
    ),
  },
  {
    key: 'labels',
    columns: searchableColumns.value.filter((column) => column.is_label && matches(column.header)),
  },
  {
    key: 'metrics',
    columns: searchableColumns.value.filter(
      (column) => isNumericReportColumn(column) && !column.is_label && matches(column.header),
    ),
  },
])
const empty = computed(
  () => !filteredSaved.value.length && groups.value.every((group) => !group.columns.length),
)

function matches(value: string): boolean {
  const query = props.query.trim().toLocaleLowerCase(locale.value)
  return !query || value.toLocaleLowerCase(locale.value).includes(query)
}
</script>

<template>
  <div class="report-filters__menu rounded-md border border-border bg-surface py-xs shadow-pop">
    <div
      class="report-filters__menu-scroll max-h-[min(20rem,var(--ui-floating-available-height))] overflow-auto text-[0.75rem]"
    >
      <div v-if="filteredSaved.length" class="report-filters__group">
        <h2
          class="m-0 w-full border-b border-border px-3 py-1 text-[0.875rem] font-bold text-primary"
        >
          {{ t('reports.filters.savedFilters') }}
        </h2>
        <div
          v-for="saved in filteredSaved"
          :key="saved.id"
          class="report-filters__saved-row grid grid-cols-[minmax(0,1fr)_auto] items-center hover:bg-surface-sunken"
        >
          <button
            type="button"
            class="flex w-full cursor-pointer items-center border-0 bg-transparent px-3 py-2 text-left text-fg transition-[padding-left,background-color] duration-150 ease-[ease]"
            @click="emit('selectSaved', saved)"
          >
            {{ saved.name }}
          </button>
          <button
            type="button"
            class="mr-xs inline-flex cursor-pointer border-0 bg-transparent p-xs text-muted"
            :aria-label="t('reports.filters.deleteSavedFilter')"
            @click="emit('removeSaved', saved.id)"
          >
            <Icon name="x" :size="12" />
          </button>
        </div>
      </div>
      <div v-for="group in groups" :key="group.key" class="report-filters__group">
        <template v-if="group.columns.length">
          <h2
            class="m-0 w-full border-b border-border px-3 py-1 text-[0.875rem] font-bold text-primary"
          >
            {{ t(`reports.filters.${group.key}`) }}
          </h2>
          <button
            v-for="column in group.columns"
            :key="column.field"
            type="button"
            class="flex w-full cursor-pointer items-center border-0 bg-transparent px-3 py-2 text-left text-fg transition-[padding-left,background-color] duration-150 ease-[ease] hover:bg-surface-sunken hover:pl-6 focus-visible:bg-surface-sunken"
            @click="emit('selectColumn', column)"
          >
            <MetricLabel :field="column.field" :column="column" />
          </button>
        </template>
      </div>
      <p v-if="empty" class="report-filters__empty m-0 p-md text-center text-muted">
        {{ t('reports.filters.noOptions') }}
      </p>
    </div>
  </div>
</template>
